import { cn } from "@/lib/utils";
import {
  onCanvasesSnapshot,
  setCanvasPreviews,
  updateCanvasFields,
} from "@/store/supabaseSlice";
import { FeaturePrd, useSupabaseStore } from "@/store/zustand/supabaseStore";
import { Project } from "@/utils/types";
import {
  format,
  formatDistanceStrict,
  isWithinInterval,
  subDays,
} from "date-fns";
import { FrameIcon, PlusIcon } from "lucide-react";
import React, { memo, useCallback, useMemo, useState, useEffect } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router";
import { Tooltip } from "react-tooltip";
import { v4 as uuidv4 } from "uuid";
import { useSupabase } from "../contexts/DatabaseSyncWrapper";
import CreateCanvasModal from "../CreateCanvasModal";
import { Button } from "../ui/button";
import { CanvasContextMenu } from "./CanvasContextMenu";
import { CanvasAssignDropdown } from "./CanvasDropdown";
import { FilterState, ProjectLabel } from "./types";
import { ADMIN_USER_IDS } from "@/utils/utils";
import { useUserId } from "@/utils/utils";
import { TEMPLATE_PROJECT_PATHS } from "@/utils/templateProjects";
import NoRepositoryModal from "../NoRepositoryModal";
import CreateRepositoryModal from "../CreateRepositoryModal";
import { GitProvider } from "@/store/gitStatusSlice";
import { useKanbanActions } from "@/hooks/useKanbanActions";

export enum KanbanDefaultStates {
  BACKLOG = "Backlog",
  DESIGN_IN_PROGRESS = "Design In Progress",
  DESIGN_REVIEW = "Design Review",
  TO_DO = "To Do",
  IN_PROGRESS = "In Progress",
  REVIEW = "Review",
  DONE = "Done",
  BLOCKED = "Blocked",
}

function CanvasLabels({
  projectLabels,
  canvas,
}: {
  projectLabels: Array<ProjectLabel>;
  canvas: any;
}) {
  const labels = useMemo(
    () =>
      projectLabels?.filter((label: any) =>
        canvas?.labels?.includes(label.id)
      ) ?? [],
    [projectLabels, canvas]
  );

  return (
    <div className="flex justify-start flex-wrap gap-2 mb-2">
      {labels?.slice(0, 5)?.map((label: ProjectLabel) => (
        <div key={label?.id}>
          <div
            data-tooltip-id={`${label?.id}Tooltip`}
            data-tooltip-delay-show={800}
            className="flex gap-2 text-xs items-center rounded-md border p-1"
          >
            <div
              className="size-2.5 rounded-full"
              style={{
                backgroundColor: label?.color?.value,
              }}
            ></div>
            <span className="max-w-52 overflow-hidden text-ellipsis">
              {label?.label}
            </span>
          </div>
          <Tooltip
            id={`${label?.id}Tooltip`}
            className="flex gap-2 items-center"
          >
            <div
              className="size-2.5 rounded-full"
              style={{
                backgroundColor: label?.color?.value,
              }}
            ></div>
            <span className="text-xs">{label?.label}</span>
          </Tooltip>
        </div>
      ))}
      {labels?.length > 5 && (
        <>
          <div
            data-tooltip-id={`${canvas.id}MoreLabelsTooltip`}
            data-tooltip-delay-show={800}
            className="flex text-xs items-center rounded-md border p-1"
          >
            <div className="flex items-center">
              {labels?.slice(5, 8).map((label) => (
                <div
                  key={label?.id}
                  className={cn(
                    "size-2.5 rounded-full mr-2",
                    labels?.length === 7 &&
                      "first:-translate-x-0 -translate-x-1 m-0",
                    labels?.length > 7 &&
                      "first:-translate-x-0 -translate-x-1 last:-translate-x-2 m-0"
                  )}
                  style={{
                    backgroundColor: label?.color?.value,
                  }}
                ></div>
              ))}
            </div>
            <span>{`+ ${labels?.length - 5} label${
              labels?.length - 5 === 1 ? "" : "s"
            }`}</span>
          </div>
          <Tooltip
            id={`${canvas?.id}MoreLabelsTooltip`}
            className="flex flex-col gap-1"
          >
            {labels?.slice(5, labels?.length)?.map((label: ProjectLabel) => (
              <div
                key={label?.id}
                data-tooltip-id={`${label?.id}Tooltip`}
                data-tooltip-delay-show={800}
                className="flex gap-2 text-xs items-center rounded-md p-1"
              >
                <div
                  className="size-2.5 rounded-full"
                  style={{
                    backgroundColor: label?.color?.value,
                  }}
                ></div>
                <span className="w-60 overflow-hidden text-ellipsis">
                  {label?.label}
                </span>
              </div>
            ))}
          </Tooltip>
        </>
      )}
    </div>
  );
}

interface ColumnProps {
  stateKey: string;
  title: string;
  canvases: any[];
  onSelect: (project: any, canvas: any) => void;
  project: Project | null;
  canvasIdToPrd: Record<string, FeaturePrd>;
  updateCanvasLabels: (canvas: any, newLabels: Array<string>) => Promise<void>;
  updateProjectLabels: (
    newLabel: Omit<ProjectLabel, "id">,
    canvas?: any
  ) => Promise<void>;
  updateCanvasAssignedUserId: (
    canvas: any,
    assignedUserId: string | null
  ) => Promise<void>;
  onClickAdd: () => void;
  isDragging: boolean;
  showCreateRepo: boolean;
}

const KanbanColumn: React.FC<ColumnProps> = memo(
  ({
    stateKey,
    title,
    canvases,
    onSelect,
    project,
    canvasIdToPrd,
    onClickAdd,
    isDragging,
    showCreateRepo,
  }) => {
    if (!project) {
      // render skeleton
      return (
        <div className="flex flex-col basis-[20.65rem] gap-4 shrink-0">
          <div className="text-2xs font-semibold">{title}</div>
          <div className="flex flex-col gap-2 max-w-full grow pb-8 group rounded-md overflow-y-scroll min-h-0">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="w-full col-span-1 bg-secondary-n-950 rounded-lg p-3 flex flex-col min-h-28 justify-between animate-pulse"
              >
                <div className="flex flex-col gap-2">
                  <div className="flex flex-row items-center justify-between">
                    <div className="h-4 w-24 bg-secondary-n-900 rounded" />
                    <div className="h-6 w-6 bg-secondary-n-900 rounded-full" />
                  </div>
                  <div className="grow min-h-8">
                    <div className="h-4 w-3/4 bg-secondary-n-900 rounded" />
                  </div>
                </div>
                <div className="flex flex-row items-center justify-between gap-1">
                  <div className="h-4 w-16 bg-secondary-n-900 rounded" />
                  <div className="h-4 w-20 bg-secondary-n-900 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col basis-[20.65rem] gap-4 shrink-0">
        <div className="text-2xs font-semibold">{title}</div>
        <Droppable droppableId={stateKey} ignoreContainerClipping={true}>
          {(provided, snapshot) => (
            <div
              className={cn(
                "flex flex-col gap-2 max-w-full grow pb-8 group rounded-md overflow-y-scroll min-h-0",
                snapshot.isDraggingOver && "bg-secondary-100"
              )}
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              <div
                className={cn("flex flex-col gap-2", {
                  hidden: !canvases.length,
                })}
              >
                {canvases.map((canvas, index) => {
                  const updatedAt = new Date(canvas.last_updated);
                  const featurePrd = canvasIdToPrd[canvas.id];
                  const now = new Date();
                  const threeDaysAgo = subDays(now, 5);

                  let dateString = null;

                  if (
                    isWithinInterval(updatedAt, {
                      start: threeDaysAgo,
                      end: now,
                    })
                  ) {
                    // @ts-expect-error TS2322
                    dateString = formatDistanceStrict(updatedAt, now, {
                      addSuffix: true,
                    });
                  } else {
                    // @ts-expect-error TS2322
                    dateString = format(updatedAt, "PPP");
                  }

                  // @ts-expect-error TS18047
                  const org = project?.repoPath.split("/")[0];
                  // @ts-expect-error TS18047
                  const repo = project?.repoPath.split("/")[1];

                  return (
                    <Draggable
                      draggableId={canvas.id}
                      index={index}
                      key={canvas.id}
                    >
                      {(providedDraggable) => (
                        <CanvasContextMenu project={project} canvas={canvas}>
                          <div
                            className="data-[state=open]:bg-primary-n-900-50 w-full col-span-1 bg-secondary-n-950 hover:bg-secondary-n-950 transition-colors duration-300 rounded-lg p-3 flex flex-col min-h-28 justify-between cursor-pointer"
                            key={canvas.id}
                            onClick={() => {
                              onSelect(project, canvas);
                            }}
                            {...providedDraggable.draggableProps}
                            {...providedDraggable.dragHandleProps}
                            ref={providedDraggable.innerRef}
                          >
                            <div className="flex flex-col gap-2">
                              <div className="flex flex-row items-center justify-between">
                                <div>
                                  <div className="text-xs">
                                    <span className="text-secondary-800">
                                      {org} /
                                    </span>
                                    <span className="text-secondary-900">
                                      {" "}
                                      {repo}
                                    </span>
                                  </div>
                                </div>
                                <CanvasAssignDropdown
                                  canvas={canvas}
                                  projectId={project.id}
                                  type="edit"
                                />
                              </div>
                              <div className="grow min-h-8">
                                <div className="text-sm text-white font-medium">
                                  <span className="line-clamp-3">
                                    {featurePrd?.title ||
                                      canvas.current_branch ||
                                      canvas.docker_container_name ||
                                      project.name ||
                                      ""}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <CanvasLabels
                              projectLabels={
                                project.labels as Array<ProjectLabel>
                              }
                              canvas={canvas}
                            />
                            <div className="flex flex-row items-center justify-between gap-1">
                              <div className="flex items-center gap-1.5">
                                <div className="flex flex-row gap-x-1 items-center text-xs text-secondary-n-500">
                                  <FrameIcon className="w-3 h-3" />
                                  <span>{canvas.storyboard_count || 0}</span>
                                </div>
                              </div>
                              {dateString && (
                                <div className="flex flex-row gap-x-1 items-center text-xs text-secondary-n-500">
                                  {dateString}
                                </div>
                              )}
                            </div>
                          </div>
                        </CanvasContextMenu>
                      )}
                    </Draggable>
                  );
                })}
              </div>
              {provided.placeholder}
              <div
                className={cn(
                  "flex opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity",
                  {
                    hidden: isDragging,
                  }
                )}
              >
                <Button
                  variant="secondary"
                  className="w-full text-xs gap-1 bg-secondary-n-950 text-secondary-n-400 hover:bg-secondary-n-900 border-0"
                  size="sm"
                  onClick={onClickAdd}
                >
                  <PlusIcon className="w-3 h-3 text-secondary-n-500" />
                  {showCreateRepo ? "Create Git Repository" : "Add a Feature"}
                </Button>
              </div>
            </div>
          )}
        </Droppable>
      </div>
    );
  }
);

interface Props {
  project: Project | null;
  onSelect: (project: any, canvas: any) => void;
  filters: FilterState;
}

const CanvasKanban: React.FC<Props> = ({ onSelect, project, filters }) => {
  const canvasPreviews: { [canvasId: string]: any } = useSelector(
    (state: any) => state.supabaseStore.canvasPreviews
  );

  const userId = useUserId();

  const { supabaseClient } = useSupabase();

  const dispatch = useDispatch<any>();

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedProjectId = searchParams.get("project_id");

  const [showCreateCanvasModal, setShowCreateCanvasModal] = useState<
    string | null
  >(null);
  const [showNoRepoModal, setShowNoRepoModal] = useState(false);
  const [showCreateRepoModal, setShowCreateRepoModal] = useState(false);
  const [originatingGitProvider, setOriginatingGitProvider] =
    useState<GitProvider | null>(null);

  const updateCanvasLabels = useKanbanActions().updateCanvasLabels;
  const updateCanvasAssignedUserId =
    useKanbanActions().updateCanvasAssignedUserId;
  const updateProjectLabels = useKanbanActions().updateProjectLabels;

  // Determine if project has a repo
  const hasRepo = useMemo(() => {
    if (project && typeof project.repoPath === "string") {
      const repoPath = project.repoPath.toLowerCase();
      // Check if project has a real repository (not a template)
      const isTemplateProject = TEMPLATE_PROJECT_PATHS.some(
        (templatePath) => templatePath === repoPath
      );

      return !isTemplateProject;
    }
    return false;
  }, [project]);

  const featurePrds = useSupabaseStore((store) => store.featurePrds);
  const canvasIdToPrd = useMemo(() => {
    const prdMap: { [canvasId: string]: FeaturePrd } = {};
    Object.keys(featurePrds).forEach((prdId) => {
      const prd = featurePrds[prdId];
      prdMap[prd.canvas_id] = prd;
    });
    return prdMap;
  }, [featurePrds]);

  // Filter for the canvases
  const canvasesToShow = useMemo(() => {
    if (!selectedProjectId) {
      return [];
    }

    return Object.values(canvasPreviews).filter((canvas) => {
      const projectIdMatch = canvas.project_id === selectedProjectId;
      const prd = canvasIdToPrd[canvas.id];
      const searchMatch = filters?.search
        ? (
            prd?.title ||
            canvas.current_branch ||
            canvas.docker_container_name ||
            project?.name
          )
            .toLowerCase()
            .includes(filters?.search.toLowerCase())
        : true;
      const userIdMatch =
        !filters?.userIds.length ||
        filters?.userIds.includes(canvas.assigned_user_id);
      const labelsMatch =
        !filters?.labels.length ||
        canvas?.labels?.some((clabel: string) =>
          filters?.labels.includes(clabel)
        );

      // Hacky way to not show all the running base canvases to internal users
      const runningBaseAndNotMain =
        // @ts-expect-error TS2345
        ADMIN_USER_IDS.includes(userId) &&
        project?.name?.toLowerCase().includes("running base") &&
        canvas.current_branch !== "MAIN" &&
        !prd?.title?.startsWith("MAIN");

      return (
        projectIdMatch &&
        searchMatch &&
        userIdMatch &&
        labelsMatch &&
        !runningBaseAndNotMain
      );
    });
  }, [canvasPreviews, selectedProjectId, filters, canvasIdToPrd]);

  // Group canvases into columns
  const stateToCanvases = useMemo(() => {
    // Initialize columns with empty canvas arrays
    const stateToCanvases = {};
    Object.keys(KanbanDefaultStates).forEach((key) => {
      stateToCanvases[KanbanDefaultStates[key]] = {
        stateKey: key,
        title: KanbanDefaultStates[key],
        canvases: [],
      };
    });

    // First, group canvases into their respective columns
    canvasesToShow.forEach((canvas) => {
      const prd = canvasIdToPrd[canvas.id];
      const status = prd?.status || KanbanDefaultStates.BACKLOG;
      stateToCanvases[status].canvases.push(canvas);
    });

    // Then sort each column's canvases
    Object.keys(stateToCanvases).forEach((columnKey) => {
      const column = stateToCanvases[columnKey];

      // Sort by order first, then by last_updated for unordered cards
      column.canvases.sort((a: any, b: any) => {
        const prdA = canvasIdToPrd[a.id];
        const prdB = canvasIdToPrd[b.id];

        // If both have orders, use them
        if (prdA?.order !== undefined && prdB?.order !== undefined) {
          // @ts-expect-error TS18047
          return prdA.order - prdB.order;
        }

        // If only one has order, prioritize the ordered one
        if (prdA?.order !== undefined) return -1;
        if (prdB?.order !== undefined) return 1;

        // Neither has order, sort by last_updated (most recent first)
        const timeA = a.last_updated || "-1";
        const timeB = b.last_updated || "-1";
        return timeB.localeCompare(timeA);
      });
    });

    return stateToCanvases;
  }, [canvasesToShow, canvasIdToPrd]);

  const [isDragging, setIsDragging] = useState(false);

  const onDragEnd = useCallback(
    async (result) => {
      setIsDragging(false);
      const { destination, source, draggableId } = result;

      // dropped nowhere
      if (!destination) {
        return;
      }

      // did not move anywhere - can bail early
      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      ) {
        return;
      }

      const canvasChangedStatus =
        source.droppableId !== destination.droppableId;
      const stateKey = destination.droppableId;
      const canvasDropping = canvasPreviews[draggableId];
      const desiredCanvasIndex = destination.index;

      // Create the new list for the changed status to get all the values in order
      let newCanvasList: any[] =
        stateToCanvases[KanbanDefaultStates[stateKey]].canvases;

      if (!canvasChangedStatus) {
        newCanvasList = newCanvasList.filter(
          (canvas) => canvas.id !== canvasDropping.id
        );
      }

      newCanvasList.splice(desiredCanvasIndex, 0, canvasDropping);

      // Go through the canvases, and update the status and/or order if needed
      const prdsToUpdate = [];
      const prdsToCreate = [];
      newCanvasList.forEach((canvas, index) => {
        const prd = canvasIdToPrd[canvas.id];
        if (!prd) {
          // @ts-expect-error TS2345
          prdsToCreate.push({
            id: uuidv4().toString(),
            canvas_id: canvas.id,
            status: KanbanDefaultStates[stateKey],
            order: index,
          });
        } else if (prd.order !== index) {
          // @ts-expect-error TS2345
          prdsToUpdate.push({
            ...prd,
            order: index,
            status: KanbanDefaultStates[stateKey],
          });
        } else if (canvas.id === canvasDropping.id && canvasChangedStatus) {
          // @ts-expect-error TS2345
          prdsToUpdate.push({
            ...prd,
            status: KanbanDefaultStates[stateKey],
          });
        }
      });

      const allPrds = [...prdsToCreate, ...prdsToUpdate];

      // Update the values both in the local state and in the DB
      const sbStoreState = useSupabaseStore.getState();

      sbStoreState.onDbSnapshot("featurePrds", {
        newItems: allPrds,
        deletedItems: [],
      });

      // Now, update supabase
      // @ts-expect-error
      await supabaseClient
        .from("feature_prd")
        .upsert(allPrds, { onConflict: "canvas_id" });
    },
    [canvasIdToPrd, stateToCanvases, canvasPreviews]
  );

  const onDragStart = () => {
    setIsDragging(true);
  };

  const handleCreateRepository = (provider: "github" | "gitlab") => {
    let gitProvider: GitProvider | null = null;
    if (provider === "github") {
      gitProvider = GitProvider.GITHUB;
    } else if (provider === "gitlab") {
      gitProvider = GitProvider.GITLAB;
    }
    setOriginatingGitProvider(gitProvider);
    setShowNoRepoModal(false);
    setShowCreateRepoModal(true);
  };

  const handleButtonClick = useCallback(
    (key: string) => {
      if (hasRepo || canvasesToShow.length !== 1) {
        // If repo exists, trigger create canvas modal
        setShowCreateCanvasModal(key);
      } else {
        // If no repo and canvases.length is 1, show repository connection modal
        setShowNoRepoModal(true);
      }
    },
    [hasRepo, canvasesToShow]
  );

  return (
    <div className="px-4 flex flex-1 flex-col h-full overflow-x-auto overflow-y-hidden">
      {project !== null && (
        <div className="flex flex-row items-center gap-x-2 text-xs h-10">
          <div className="font-semibold">Canvases</div>
          <div className="text-secondary-800 leading-none">
            {canvasesToShow.length}
          </div>
        </div>
      )}
      <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
        <div className="mt-2 flex flex-row gap-4 min-h-full">
          {Object.keys(KanbanDefaultStates).map((key) => {
            const colState = stateToCanvases[KanbanDefaultStates[key]];
            return (
              <KanbanColumn
                {...colState}
                project={project}
                canvasIdToPrd={canvasIdToPrd}
                key={key}
                onSelect={onSelect}
                isDragging={isDragging}
                onClickAdd={() => handleButtonClick(key)}
                updateCanvasLabels={updateCanvasLabels}
                updateProjectLabels={updateProjectLabels}
                updateCanvasAssignedUserId={updateCanvasAssignedUserId}
                showCreateRepo={canvasesToShow.length === 1 && !hasRepo}
              />
            );
          })}
        </div>
      </DragDropContext>

      <CreateCanvasModal
        openCanvasState={
          showCreateCanvasModal
            ? KanbanDefaultStates[showCreateCanvasModal]
            : showCreateCanvasModal
        }
        // @ts-expect-error TS2322
        setOpenCanvasState={setShowCreateCanvasModal}
        project={project}
        createIndex={
          // @ts-expect-error TS2538
          stateToCanvases?.[KanbanDefaultStates[showCreateCanvasModal]]
            ?.canvases?.length || 0
        }
      />

      <NoRepositoryModal
        isOpen={showNoRepoModal}
        onClose={() => setShowNoRepoModal(false)}
        onCreateRepository={handleCreateRepository}
      />

      {showCreateRepoModal && canvasesToShow && canvasesToShow.length > 0 && (
        <CreateRepositoryModal
          canvasId={canvasesToShow[0].id}
          isOpen={showCreateRepoModal}
          onClose={() => {
            setShowCreateRepoModal(false);
            setOriginatingGitProvider(null);
          }}
          onRepoCreated={() => {
            setShowCreateRepoModal(false);
            setOriginatingGitProvider(null);
          }}
          initialSelectedOrg={null}
          originatingProvider={originatingGitProvider}
        />
      )}
    </div>
  );
};

export default React.memo(CanvasKanban);
